// Helpers for cleaning Mapbox layers/sources that the live-tracking
// view-only map owns. Pulled out as pure functions so the matching
// rules are testable in isolation — the surrounding map effect is
// hard to unit-test because it depends on a live mapbox-gl instance.

// Live-tracking map adds zone polygons + labels under predictable id
// prefixes. Anything matching these prefixes that's still on the map
// when the effect re-runs is treated as orphaned and removed, even if
// the component's `currentAreaIds` ref doesn't know about it.
//
// The "boundary-*" / "festival-boundary-*" prefixes remain here as a
// belt-and-braces net, but the actual "festival boundary shows on the
// read-only map" bug was NOT a layer leak from LiveTrackingSetup (separate
// route, separate map instance). Its root cause was a shared React Query
// cache key filled with a select("*") superset; that is now fixed at the
// source (private cache namespace + selectVisibleTrackingAreas allowlist in
// lib/trackingAreas), so a boundary is never fetched or drawn here.
export function isOrphanedTrackingLayer(layerId: string): boolean {
  if (!layerId) return false;
  return (
    layerId.startsWith("area-") ||
    layerId.startsWith("boundary-") ||
    layerId.startsWith("festival-boundary-")
  );
}

export function isOrphanedTrackingSource(sourceId: string): boolean {
  if (!sourceId) return false;
  return (
    sourceId.startsWith("area-") ||
    sourceId.startsWith("boundary-") ||
    sourceId.startsWith("festival-boundary-")
  );
}

// Readiness gate shared by every effect that draws onto the live-tracking
// map (crew markers, festival areas, the producer dot). The map is
// initialised asynchronously — the mapbox token is fetched before
// `new mapboxgl.Map`, and the `load` event (which flips mapLoaded) fires
// later still — so data can arrive before the map can be drawn on. An
// effect that gates only on the map instance existing, without also
// requiring mapLoaded in BOTH the guard and its dependency array, can run
// early, bail, and never re-run until an unrelated dependency changes.
// That was the "crew markers only appear after I click someone" bug.
//
// Keeping this as one predicate means the three drawing effects gate
// identically; each still adds its own data check (e.g. `&& crewLocations`)
// on top.
export function isMapReady(args: { hasMap: boolean; mapLoaded: boolean }): boolean {
  return args.hasMap && args.mapLoaded;
}
